import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCreateContract } from '@/hooks/use-create-contract';
import {
  ColocData,
  ContractCreateData,
  DatesData,
  RoomData,
} from '@/features/admin/contracts/types/contract-create';
import { ColocFormData, RoomFormData, DatesFormData, FormState } from '../types';
import { useLeaveConfirmation } from '@/hooks/use-leave-confirmation';
import { showToast } from '@/services/toast-service';

export const useContractWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const step = location.pathname.split('/').pop() || 'add-coloc';

  const { createContract, isCreating, error, showSuccessModal, setShowSuccessModal } =
    useCreateContract();
  const [contractData, setContractData] = useState<{
    coloc?: ColocData;
    room?: RoomData;
    dates?: DatesData;
  }>({});

  const [formState, setFormState] = useState<FormState>({
    colocData: {
      colocFirstName: '',
      colocLastName: '',
      colocEmail: '',
      colocMobileCountryCode: '32',
      colocMobileNumber: '',
      colocLocale: 'en',
      colocId: '',
      colocBirthday: '',
      colocBirthplace: '',
      securityDepositAmount: '',
      securityDepositAmountPaid: '',
    },
    roomData: {
      houseId: '',
      roomId: '',
      accessCode: '',
    },
    datesData: {
      moveInDate: null,
      moveOutDate: null,
    },
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { showConfirmation, cancelNavigation, confirmNavigation, showingConfirmation } =
    useLeaveConfirmation({
      shouldConfirm: hasUnsavedChanges && !isCreating,
      message:
        'You have unsaved changes. Are you sure you want to leave this page? Your progress will be lost.',
    });

  useEffect(() => {
    if (step === 'pick-room' && !contractData.coloc) {
      navigate('/admin/contract-wizard/add-coloc', { replace: true });
      return;
    }

    if (step === 'pick-dates' && (!contractData.coloc || !contractData.room)) {
      navigate('/admin/contract-wizard/add-coloc', { replace: true });
      return;
    }
  }, [step, contractData, navigate]);

  useEffect(() => {
    if (error) {
      console.error('Contract creation error:', error);
    }
  }, [error]);

  useEffect(() => {
    if (contractData.coloc || contractData.room || contractData.dates) {
      setHasUnsavedChanges(true);
    }
  }, [contractData]);

  const handleColocSubmit = (data: ColocFormData) => {
    const colocData: ColocData = {
      firstName: data.colocFirstName,
      lastName: data.colocLastName,
      email: data.colocEmail,
      mobileCountryCode: data.colocMobileCountryCode,
      mobileNumber: data.colocMobileNumber,
      locale: data.colocLocale,
      id: data.colocId,
      birthday: data.colocBirthday
        ? new Date(data.colocBirthday).toISOString().split('T')[0]
        : undefined,
      birthplace: data.colocBirthplace,
      securityDepositAmount: data.securityDepositAmount,
      securityDepositAmountPaid: data.securityDepositAmountPaid,
    };

    setFormState((prev) => ({
      ...prev,
      colocData: data,
    }));
    setContractData((prev) => ({ ...prev, coloc: colocData }));
    navigate('/admin/contract-wizard/pick-room');
  };

  const handleRoomSubmit = (data: RoomFormData) => {
    const roomData: RoomData = {
      houseId: data.houseId,
      roomId: data.roomId,
      accessCode: data.accessCode,
    };

    setFormState((prev) => ({
      ...prev,
      roomData: data,
    }));
    setContractData((prev) => ({ ...prev, room: roomData }));
    navigate('/admin/contract-wizard/pick-dates');
  };

  const handleDatesSubmit = async (data: DatesFormData) => {
    const datesData: DatesData = {
      moveInDate: data.moveInDate,
      moveOutDate: data.moveOutDate,
    };

    setFormState((prev) => ({
      ...prev,
      datesData: data,
    }));
    setContractData((prev) => ({ ...prev, dates: datesData }));

    if (!contractData.coloc || !contractData.room) {
      showToast.error({ text: 'Missing contract data. Please start the process again.' });
      navigate('/admin/contract-wizard/add-coloc');
      return;
    }

    const completeContractData: ContractCreateData = {
      coloc: contractData.coloc,
      room: contractData.room,
      dates: datesData,
    };

    const success = await createContract(completeContractData);

    if (success) {
      setHasUnsavedChanges(false);
    }
  };

  const safeOnClose = () => {
    if (hasUnsavedChanges) {
      showConfirmation();
    } else {
      navigate('/admin/contracts');
    }
  };

  const safePrevious = () => {
    if (step === 'pick-room') {
      navigate('/admin/contract-wizard/add-coloc');
    } else if (step === 'pick-dates') {
      navigate('/admin/contract-wizard/pick-room');
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate('/admin/contracts');
  };

  return {
    step,
    formState,
    isCreating,
    showSuccessModal,
    showingConfirmation,
    handleColocSubmit,
    handleRoomSubmit,
    handleDatesSubmit,
    safeOnClose,
    safePrevious,
    handleSuccessModalClose,
    cancelNavigation,
    confirmNavigation,
  };
};
